import React, { useState } from "react";

import { useAgents } from "@/shared/api/agents";
import { PHONE_TEXTS_OFF, useAgentUsers, useOnboardingFlow, type PeopleFilter } from "@/shared/api/onboarding";
import { Pane } from "@/shared/components/settings-atoms";
import { Segmented } from "@/shared/components/teams-ui";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { STEPS } from "./blocks";
import { FlowEditor } from "./components/FlowEditor";
import { PeopleTable } from "./components/PeopleTable";
import { PersonSheet } from "./components/PersonSheet";
import { useFlowDraft } from "./hooks/useFlowDraft";

import type { Agent, AgentUser, OnboardingFlow } from "@/shared/types";

type Tab = "flow" | "people";
const EVERYONE: PeopleFilter = { status: null, q: "" };

/* Agent Settings › People › Onboarding: what a person does before the agent answers them —
   questions, a sign-in — on a line (the Flow tab), and everyone who has come (the People
   tab). People take the flow on the agent's client app and in its extension. Who may come
   at all, and with what limits, is Policies'. */
export default function OnboardingSection({ agent, onOpenThread }: {
  agent: Agent;
  /** A conversation picked from a person's sheet. */
  onOpenThread?: (threadId: string) => void;
}) {
  const agents = useAgents();
  const row = agents.data?.find((found) => found.id === agent.id);
  const flow = useOnboardingFlow(row?.agent_onboarding_flow_id);

  return (
    <Pane
      title="Onboarding"
      desc="What people do before this agent answers them — questions, a sign-in — and everyone who has come. People take it on the client app and in the extension."
    >
      {flow.data ? (
        <Onboarding key={flow.data.id} agent={agent} flow={flow.data} onOpenThread={onOpenThread} />
      ) : (
        <p className="m-0 text-[12.5px] text-[var(--text-tertiary)]">{flow.isError ? "The flow couldn't be loaded." : "Loading…"}</p>
      )}
    </Pane>
  );
}

function Onboarding({ agent, flow: saved, onOpenThread }: {
  agent: Agent;
  flow: OnboardingFlow;
  onOpenThread?: (threadId: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("flow");
  const [person, setPerson] = useState<AgentUser | null>(null);
  const flow = useFlowDraft(saved, agent.name);
  // the table's own first page when it is unfiltered: one read for both
  const everyone = useAgentUsers(agent.id, EVERYONE);
  const counted = everyone.data?.pages.flat().length;
  const refused = flow.refused;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Segmented
          role="tablist"
          label="Onboarding"
          value={tab}
          onChange={setTab}
          options={[
            { value: "flow", label: <>Flow <Count>{flow.steps.length}</Count></> },
            { value: "people", label: <>People <Count>{counted === undefined ? "" : `${counted}${everyone.hasNextPage ? "+" : ""}`}</Count></> },
          ]}
        />
        <span className="flex-1" />
        {tab === "flow" && (
          <div className="flex flex-wrap items-center gap-2">
            {flow.dirty && (
              <>
                <span className="text-[12px] text-[var(--text-tertiary)] whitespace-nowrap">Unsaved changes</span>
                <GhostButton className="disabled:opacity-40 disabled:pointer-events-none" disabled={flow.saving} onClick={flow.discard}>
                  Discard
                </GhostButton>
              </>
            )}
            <BrandButton
              className="disabled:opacity-40 disabled:pointer-events-none"
              disabled={!flow.dirty || !!flow.issue || flow.saving}
              title={flow.issue ?? undefined}
              onClick={flow.save}
            >
              {flow.saving ? "Saving…" : "Save"}
            </BrandButton>
          </div>
        )}
      </div>

      {tab === "flow" ? (
        <>
          {flow.dirty && flow.issue && <Banner>{flow.issue}</Banner>}
          {refused && (
            <Banner warn={refused.code === PHONE_TEXTS_OFF}>
              Not saved: {refused.message}
              {refused.code === PHONE_TEXTS_OFF && ` Remove “${STEPS.phone.name}” to save the rest.`}
              {refused.request_id && <span className="block font-mono text-[11px] opacity-70">ref {refused.request_id}</span>}
            </Banner>
          )}
          <FlowEditor agent={agent} flow={flow} />
          <p className="m-0 mt-4 max-w-[720px] text-[11.5px] leading-[1.6] text-[var(--text-tertiary)]">
            People take the flow on {agent.name}'s client app and in its Chrome extension. Someone new on WhatsApp, Telegram
            or Slack is sent to the client app first, and what they wrote is answered once they finish. Saved flows reach
            people the next time they come.
          </p>
        </>
      ) : (
        <PeopleTable agentId={agent.id} flow={saved} onOpen={setPerson} />
      )}

      {person && (
        <PersonSheet person={person} flow={saved} onClose={() => setPerson(null)} onOpenThread={onOpenThread} />
      )}
    </>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return <span className="font-mono tabular-nums text-[11px] text-[var(--text-quaternary)]">{children}</span>;
}

/** What stops a save, or why the last one was refused: above the flow. */
function Banner({ warn = false, children }: { warn?: boolean; children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className={`mb-3 rounded-[10px] px-3 py-2 text-[12.5px] leading-[1.5] ring-1 ${
        warn
          ? "bg-[var(--warn-bg)] ring-[var(--warn-ring)] text-[var(--warn-text)]"
          : "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]"
      }`}
    >
      {children}
    </div>
  );
}
